import React, { useMemo } from "react";
import useTranslation from "../../i18n/useTranslation";

export default function FilePicker({
                                       scopeId,
                                       colors,
                                       label,
                                       hint,
                                       accept = "image/*",
                                       value, // File | null
                                       previewUrl, // string | null
                                       onChangeFile,
                                       onClear,
                                   }) {
    const { t } = useTranslation();

    const inputId = useMemo(
        () => `${scopeId}-file-${Math.random().toString(36).slice(2, 9)}`,
        [scopeId]
    );

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const f = e.dataTransfer?.files?.[0];
        if (f) onChangeFile(f);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <>
            <div className="form-group kbola-field">
                {label ? <label className="kbola-label">{label}</label> : null}

                <input
                    id={inputId}
                    className="kbola-file-native"
                    type="file"
                    accept={accept}
                    onChange={(e) => onChangeFile(e.target.files?.[0] || null)}
                />

                <div
                    className="kbola-file-shell"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    role="group"
                    aria-label={t("filePicker.ariaLabel")}
                >
                    <label htmlFor={inputId} className="kbola-file-btn">
                        <i className="fas fa-upload mr-2" />
                        {t("filePicker.chooseFile")}
                    </label>

                    <div className="kbola-file-meta">
                        {value?.name ? (
                            <span className="kbola-file-name" title={value.name}>
                                {value.name}
                            </span>
                        ) : previewUrl ? (
                            <span
                                className="kbola-file-name"
                                title={t("filePicker.existingImage")}
                            >
                                {t("filePicker.existingImage")}
                            </span>
                        ) : (
                            <span className="kbola-file-placeholder">
                                {t("filePicker.noFileSelected")}
                            </span>
                        )}

                        <div className="kbola-file-subhint">
                            {t("filePicker.dragDropHint")}
                        </div>
                    </div>

                    {value || previewUrl ? (
                        <button
                            type="button"
                            className="kbola-file-clear"
                            onClick={onClear}
                            title={t("filePicker.remove")}
                            aria-label={t("filePicker.remove")}
                        >
                            <i className="fas fa-times" />
                        </button>
                    ) : null}
                </div>

                {hint ? <small className="kbola-hint">{hint}</small> : null}

                {previewUrl ? (
                    <div className="kbola-file-preview">
                        <div className="kbola-file-thumb">
                            <img
                                src={previewUrl}
                                alt={t("filePicker.logoPreview")}
                            />
                        </div>

                        <div className="kbola-file-preview-text">
                            <div style={{ fontWeight: 900, color: colors.primary }}>
                                {t("filePicker.preview")}
                            </div>
                            <div style={{ fontSize: 12, color: colors.primary }}>
                                {value?.name || t("filePicker.existingImage")}
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>

            <style>{`
                .kbola-file-native{
                    position:absolute;
                    width:1px;
                    height:1px;
                    padding:0;
                    margin:-1px;
                    overflow:hidden;
                    clip: rect(0,0,0,0);
                    white-space:nowrap;
                    border:0;
                }

                .kbola-file-shell{
                    display:flex;
                    align-items:center;
                    gap:10px;
                    border-radius: 14px;
                    border: 1.5px dashed ${colors.primary};
                    background: rgba(255,255,255,0.85);
                    padding: 10px 10px;
                    transition: border-color .12s ease, box-shadow .12s ease, transform .12s ease;
                }

                .kbola-file-shell:hover{
                    border-color: ${colors.primary};
                    box-shadow: 0 12px 26px rgba(0,0,0,0.06);
                    transform: translateY(-1px);
                }

                .kbola-file-shell:focus-within{
                    border-color: ${colors.primary};
                    box-shadow: 0 0 0 3px rgba(var(--kbs-primary-rgb, 0,173,255), 0.26);
                }

                .kbola-file-btn{
                    display:inline-flex;
                    align-items:center;
                    justify-content:center;
                    gap:8px;
                    padding: 10px 12px;
                    border-radius: 12px;
                    background: ${colors.primary};
                    color: ${colors.textOnPrimary};
                    font-weight: 900;
                    cursor:pointer;
                    margin:0;
                    user-select:none;
                    white-space:nowrap;
                    border: 1px solid rgba(20,21,26,0.15);
                }

                .kbola-file-meta{
                    flex:1;
                    min-width: 0;
                }

                .kbola-file-name{
                    display:inline-block;
                    max-width: 100%;
                    overflow:hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    font-weight: 800;
                    color:${colors.textOnPrimary};
                    background: ${colors.primary};
                    border: 1px solid rgba(var(--kbs-primary-rgb, 0,173,255), 0.35);
                    padding: 6px 10px;
                    border-radius: 999px;
                    font-size: 12px;
                }

                .kbola-file-placeholder{
                    color: ${colors.primary};
                    font-weight: 700;
                    font-size: 12px;
                }

                .kbola-file-subhint{
                    margin-top: 4px;
                    font-size: 11px;
                    color: ${colors.secondary};
                    font-weight: 700;
                }

                .kbola-file-clear{
                    width: 38px;
                    height: 38px;
                    border-radius: 12px;
                    border: 1px solid rgba(20,21,26,0.10);
                    background: ${colors.primary};
                    color: ${colors.textOnPrimary};
                    display:flex;
                    align-items:center;
                    justify-content:center;
                    cursor:pointer;
                }

                .kbola-file-preview{
                    display:flex;
                    align-items:center;
                    gap:12px;
                    margin-top: 10px;
                }

                .kbola-file-thumb{
                    width: 72px;
                    height: 72px;
                    border-radius: 14px;
                    overflow:hidden;
                    border: 1px solid rgba(20,21,26,0.10);
                    box-shadow: 0 12px 24px rgba(0,0,0,0.06);
                    background:#fff;
                }

                .kbola-file-thumb img{
                    width:100%;
                    height:100%;
                    object-fit: cover;
                    display:block;
                }

                .kbola-label{
                    font-size: 12.5px;
                    font-weight: 800;
                    color: rgba(20,21,26,0.70);
                    margin-bottom: 6px;
                    display: block;
                }
            `}</style>
        </>
    );
}